import { VALID_TEMPLATE_PROP_FIELD_TYPES } from '../../../../lib';
import { useDynamicEntityCMSDataContext, useDynamicEntityStoreContext } from '../../../../contexts';

import CategorySelectorInput from '../CategorySelectorInput';
import TextAreaInput from '../../../../../inputs/form-inputs/TextAreaInput';
import SwitchInput from '../../../../../inputs/form-inputs/SwitchInput';
import FormattedTextInput from '../../../../../inputs/form-inputs/FormattedTextInput';
import NumberInput from '../../../../../inputs/form-inputs/NumberInput';
import RangeInput from '../../../../../inputs/form-inputs/RangeInput';

import ImageInput from '../ImageInput';
import FormsInput from '../FormsInput';
import ModulesInput from '../ModulesInput';
import ObjectInput from '../ObjectInput';
import TemplatesInput from '../TemplatesInput';
import InputWrapper from '../InputWrapper';
import RepeaterInput from '../RepeaterInput';

export default function TemplatePropField(props) {
  const {
    ancestors,
    component,
    templateItem,
    name,
    type,
    options = [],
    template_schema,
    template_item_label,
    object_schema,
    category_id,
    category_internal_build_name,
    allowMultipleOptions,
    rangeProps,
  } = props;

  const { categoriesLinked, isModule } = useDynamicEntityCMSDataContext();

  // This is the full logical path to the specific field inside a specific template item.
  // e.g., ['props', 'Field7', '2cc7a506-2d7c-4dea-9083-97595cbe7ec4', 'Field7.1']
  const fullPath = [...ancestors, templateItem.id, name];

  const value = templateItem[name];

  const updateIndividualContent = useDynamicEntityStoreContext((state) => state.updateIndividualContent);
  const removeDynamicPageModule = useDynamicEntityStoreContext((state) => state.removeDynamicPageModule);
  const addDynamicPageModule = useDynamicEntityStoreContext((state) => state.addDynamicPageModule);

  const handleChange = (newValue) => updateIndividualContent(component.id, fullPath, newValue);

  const handleModulesChange = (event) => {
    handleChange(event.value);

    if (!event.value) {
      removeDynamicPageModule(`${templateItem.id}-${name}`);
      return;
    }

    addDynamicPageModule(event.value, `${templateItem.id}-${name}`);
  };

  const renderOptions = {
    string: () => {
      if (options && options.length) {
        return (
          <select className="form-select" value={value || ''} onChange={(e) => handleChange(e.target.value)}>
            <option value="" disabled>
              Select an option
            </option>
            {options.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      return <TextAreaInput className="form-control text-area-fit" value={value || ''} onChange={(e) => handleChange(e.target.value)} rows={1} />;
    },
    boolean: () => <SwitchInput checked={!!value} onChange={(e) => handleChange(e.target.checked)} />,
    array: () => (
      <select className="form-select" value={JSON.stringify(value) || ''} onChange={(e) => handleChange(JSON.parse(e.target.value))}>
        <option value="" disabled>
          Select an option
        </option>
        {options.length ? (
          options.map((option, index) => (
            <option key={index} value={JSON.stringify(option.value)}>
              {option.label}
            </option>
          ))
        ) : (
          <option value="">No options found.</option>
        )}
      </select>
    ),
    number: () => <NumberInput value={value || 0} onChange={(e) => handleChange(Number(e.target.value))} />,
    formatted_text: () => <FormattedTextInput value={value || ''} onEditorChange={(value) => handleChange(value)} />,
    file: () => <ImageInput id={`${component.id}-${templateItem.id}-${name}`} value={value} onChange={(value) => handleChange(value)} />,
    forms: () => <FormsInput {...{ value, handleFormsChange: (e) => handleChange(e.value) }} />,
    modules: () => <ModulesInput {...{ value, handleModulesChange }} />,
    object: () => (
      <ObjectInput
        {...{
          ancestors: fullPath,
          component,
          onChange: handleChange,
          value,
          object_schema,
        }}
      />
    ),
    template: () => <TemplatesInput {...{ ancestors: fullPath, component, onChange: handleChange, template_schema, template_item_label, value }} />,
    repeater: () => <RepeaterInput {...{ onChange: handleChange, value }} />,
    range: () => <RangeInput {...{ onChange: handleChange, value }} {...rangeProps} />,
  };

  if (!name || !type) {
    return (
      <InputWrapper {...props} label="INVALID PROP">
        <span>This prop does not have the required 'name' or 'type' field.</span>
      </InputWrapper>
    );
  }

  if (category_id || category_internal_build_name) {
    const category = categoriesLinked.find((c) => c.id === category_id || c.internal_build_name === category_internal_build_name);

    return (
      <InputWrapper {...props}>
        {category ? <CategorySelectorInput {...{ value, onChange: handleChange, category, allowMultipleOptions }} /> : <span>Error: Category not found.</span>}
      </InputWrapper>
    );
  }

  if (isModule && type === 'modules') {
    return (
      <InputWrapper {...props}>
        <span>You are not allowed to link to a module within a module.</span>
      </InputWrapper>
    );
  }

  if (!VALID_TEMPLATE_PROP_FIELD_TYPES.includes(type)) {
    return (
      <InputWrapper {...props}>
        <span>This prop is not configured properly.</span>
      </InputWrapper>
    );
  }

  return <InputWrapper {...props}>{renderOptions[type]()}</InputWrapper>;
}
